import React from "react";
import { ShoppingBag, MessageSquare, Megaphone, Gauge } from "lucide-react";
import { formatDateTime } from "@/lib/datetime";
import { Card, LinkedBy, Open, gbp } from "./AnalyticsParts";

export function Purchases({ orders }) {
  return (
    <Card title="Recent purchases" icon={ShoppingBag} testid="analytics-purchases">
      {!orders.length ? <p className="text-sm text-slate-400">No WooCommerce or shop purchases linked.</p> : (
        <div className="divide-y divide-slate-100">
          {orders.slice(0, 10).map((o, i) => (
            <div key={i} className="py-2.5 flex items-start justify-between gap-3" data-testid={`analytics-order-${i}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{o.source} #{o.ref} <span className="text-xs text-slate-400 font-normal">· {o.status}</span></div>
                {!!o.items.length && <div className="text-xs text-slate-500 break-words">{o.items.map((x) => `${x.qty ? `${x.qty}× ` : ""}${x.name}`).join(", ")}</div>}
                <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">{formatDateTime(o.date)} <LinkedBy v={o.linked_by} />{!o.counted && <span>not counted in spend</span>}</div>
              </div>
              <div className="text-right shrink-0"><div className="text-sm font-semibold text-slate-900">{gbp(o.total)}</div><Open to={o.task_legacy_id ? `/tasks/${o.task_legacy_id}` : o.link} testid={`analytics-order-open-${i}`} /></div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function Engagement({ a }) {
  const wa = a.whatsapp;
  return (
    <Card title="Campaign engagement" icon={Megaphone} testid="analytics-engagement">
      <div className="space-y-3 text-sm">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">WhatsApp campaigns</div>
          {!wa.campaigns.length ? <p className="text-slate-400 text-xs">No live WhatsApp campaigns sent to this customer.</p> : wa.campaigns.map((c, i) => (
            <div key={i} className="flex justify-between gap-2" data-testid={`analytics-campaign-${i}`}><span>{c.name || c.campaign_id} <span className="text-xs text-slate-400">· {c.status}{c.clicked ? " · clicked" : ""}{c.replied ? " · replied" : ""}</span></span><Open to={c.link} /></div>
          ))}
        </div>
        {!!wa.superchat.length && <div data-testid="analytics-superchat">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Superchat history (imported)</div>
          {wa.superchat.map((s, i) => (
            <div key={i} className="text-xs text-slate-600 break-words">
              {!!s.labels.length && <div>Labels: {s.labels.join(", ")}</div>}
              {!!s.campaign_history.length && <div>Campaign IDs: {s.campaign_history.join(", ")}</div>}
            </div>))}
        </div>}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Email newsletter</div>
          <p className="text-xs text-slate-600">{a.email.newsletter_subscribed ? "Subscribed" : "Not subscribed"} · opens and clicks not tracked yet</p>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Confirmed campaign → sale links</div>
          {!a.attribution.length ? <p className="text-xs text-slate-400">None confirmed. Purchases are never credited to a campaign without a tracking code or linked customer.</p> : a.attribution.map((x, i) => (
            <div key={i} className="flex justify-between gap-2 text-xs" data-testid={`analytics-attribution-${i}`}><span>{x.campaign_name || x.campaign_id} → job {x.job_legacy_id}{x.sale_value ? ` · ${gbp(x.sale_value)}` : ""}</span><Open to={x.link} /></div>
          ))}
        </div>
      </div>
    </Card>
  );
}

export function Conversations({ items }) {
  return (
    <Card title="Conversations" icon={MessageSquare} testid="analytics-conversations">
      {!items.length ? <p className="text-sm text-slate-400">No SMS conversations linked.</p> : items.map((c, i) => (
        <div key={c.id} className="flex justify-between gap-2 py-1.5 text-sm" data-testid={`analytics-conversation-${i}`}>
          <span>{(c.channel || "").toUpperCase()}{c.job_id ? ` · job ${c.job_id}` : ""} <span className="text-xs text-slate-400">· {c.outbound} sent · {c.inbound} received · {formatDateTime(c.last_message_at)}</span></span>
          <Open to={c.link} />
        </div>
      ))}
    </Card>
  );
}

export function Tracking({ items }) {
  return (
    <Card title="Data coverage" icon={Gauge} testid="analytics-tracking">
      <ul className="space-y-1.5">
        {items.map((t, i) => (
          <li key={i} className="flex items-start justify-between gap-3 text-xs" data-testid={`analytics-tracking-${i}`}>
            <span className="text-slate-600">{t.metric}{t.note && <span className="block text-slate-400">{t.note}</span>}</span>
            <span className={`shrink-0 rounded-full px-2 py-0.5 font-semibold ${t.status === "available" ? "bg-emerald-50 text-emerald-700" : t.status === "not_tracked" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
              {t.status === "not_tracked" ? "Not tracked yet" : t.status === "available" ? "Available" : t.status}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
